import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label"; 
import { Textarea } from "../ui/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem } from "../ui/command";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Dog, Session, ActivityType } from "../../types";
import { createSession, updateSession } from "../../utils/api";
import { X, Footprints, Search, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import CanicrossIcon from "../icon/CanicrossIcon";

interface SessionFormProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSessionCreated?: (newSession: Session) => void;
  onSessionUpdated?: (updatedSession: Session) => void;
  dashboardId?: string; // Make dashboardId optional
  trainerDogs: Dog[];
  sessionToEdit?: Session | null;
  initialDate?: Date;
}

const activityConfigs = {
  mantrailing: { label: "Mantrailing", icon: Search },
  hiking: { label: "Randonnée", icon: Footprints },
  canicross: { label: "Canicross", icon: CanicrossIcon },
};

export function SessionForm({
  isOpen,
  onOpenChange,
  onSessionCreated,
  onSessionUpdated,
  dashboardId,
  trainerDogs,
  sessionToEdit,
  initialDate,
}: SessionFormProps) {
  const [startTime, setStartTime] = useState(new Date().toISOString().slice(0, 16));
  const [endTime, setEndTime] = useState(new Date().toISOString().slice(0, 16));
  const [location, setLocation] = useState("");
  const [comment, setComment] = useState("");
  const [activityType, setActivityType] = useState<ActivityType>("mantrailing");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDogIds, setSelectedDogIds] = useState<string[]>([]); // Add state for selected dogs

  const isEditing = !!sessionToEdit;

  useEffect(() => {
    if (isOpen) {
      if (isEditing && sessionToEdit) {
        setStartTime(new Date(sessionToEdit.startTime).toISOString().slice(0, 16));
        setEndTime(new Date(sessionToEdit.endTime).toISOString().slice(0, 16));
        setLocation(sessionToEdit.location || "");
        setComment(sessionToEdit.comment || "");
        setActivityType(sessionToEdit.activityType || "mantrailing");
        setSelectedDogIds(sessionToEdit.plannedDogs.map(dog => dog._id));
      } else {
        const startDate = initialDate ? new Date(initialDate) : new Date();
        const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours later
        setStartTime(startDate.toISOString().slice(0, 16));
        setEndTime(endDate.toISOString().slice(0, 16));
        setLocation("");
        setComment("");
        setActivityType("mantrailing");
        setSelectedDogIds([]);
      }
    }
  }, [isOpen, isEditing, sessionToEdit, initialDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const currentDashboardId = dashboardId || sessionToEdit?.dashboardId;
    if (!currentDashboardId) {
      toast.error("Impossible de créer ou modifier la session : ID du tableau de bord manquant.");
      return;
    }

    setIsLoading(true);
    try {
      const sessionData = {
        dashboardId: currentDashboardId,
        startDate: startTime.split('T')[0], // Derive startDate from startTime
        startTime,
        endTime,
        location,
        comment,
        activityType,
        plannedDogs: selectedDogIds,
      };
      
      if (isEditing && sessionToEdit) {
        const updated = await updateSession(sessionToEdit._id, sessionData as any);
        onSessionUpdated?.(updated);
        toast.success("Session mise à jour avec succès !");
      } else {
        const newSession = await createSession({ ...sessionData, assignedTrainers: [] } as any);
        onSessionCreated?.(newSession);
        toast.success("Session créée avec succès !");
      }

      onOpenChange(false);
    } catch (error) {
      const action = isEditing ? "la mise à jour" : "la création";
      console.error(`Failed to ${action} session:`, error);
      toast.error(`Erreur lors de ${action} de la session.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? "Modifier la Session" : "Nouvelle Session d'Entraînement"}</DialogTitle>
            <DialogDescription>
              Planifiez une nouvelle session.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label className="text-sm font-medium">Type d'activité</Label> 
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal">
                    {React.createElement(activityConfigs[activityType].icon, { className: "mr-2 h-4 w-4" })}
                    <span>{activityConfigs[activityType].label}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {Object.entries(activityConfigs).map(([key, { label, icon }]) => (
                    <DropdownMenuItem key={key} onSelect={() => setActivityType(key as ActivityType)}>
                      {React.createElement(icon, { className: "mr-2 h-4 w-4" })}
                      <span>{label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="space-y-2">
              <Label htmlFor="startTime">Début</Label>
              <Input id="startTime" type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">Fin</Label>
              <Input id="endTime" type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Lieu</Label>
              <Input id="location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Lieu de la session" />
            </div>
            <div className="space-y-2">
              <Label>Chiens prévus</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal h-auto min-h-10 flex-wrap">
                    {selectedDogIds.length > 0 ? selectedDogIds.map(id => {
                      const dog = trainerDogs.find(d => d._id === id);
                      return (
                        <Badge key={id} variant="secondary" className="m-1">
                          {dog?.name}
                          <X className="ml-1 h-3 w-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); setSelectedDogIds(ids => ids.filter(i => i !== id)); }} />
                        </Badge>
                      );
                    }) : "Sélectionner des chiens..."}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto">
                  <Command>
                    <CommandInput placeholder="Rechercher un chien..." />
                    <CommandEmpty>Aucun chien trouvé.</CommandEmpty>
                    <CommandGroup>
                      {trainerDogs.map(dog => (
                        <CommandItem key={dog._id} onSelect={() => {
                          setSelectedDogIds(ids => ids.includes(dog._id) ? ids.filter(i => i !== dog._id) : [...ids, dog._id]);
                        }}>
                          <Check className={`mr-2 h-4 w-4 ${selectedDogIds.includes(dog._id) ? 'opacity-100' : 'opacity-0'}`} />
                          <span>{dog.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="comment">Commentaires</Label>
              <Textarea id="comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Notes sur la session..." />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Annuler</Button>
            <Button type="submit" disabled={isLoading}>{isLoading ? "Enregistrement..." : (isEditing ? "Mettre à jour" : "Créer la session")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
